import { z } from "zod";

/**
 * The desktop app's in-app updates. Release CI stages each installer in a private R2 bucket and
 * then publishes `latest.json`; the Worker serves both to signed-in desktop apps only.
 */
export const desktopVersionSchema = z.string().regex(/^(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)$/, "Desktop versions must use major.minor.patch");
export const desktopUpdateTargetSchema = z.enum(["win32-x64", "darwin-universal", "linux-x64"]);
export type DesktopUpdateTarget = z.infer<typeof desktopUpdateTargetSchema>;

export const desktopUpdateAssetFormats = {
  "win32-x64": { extension: "exe", contentType: "application/vnd.microsoft.portable-executable" },
  "darwin-universal": { extension: "dmg", contentType: "application/x-apple-diskimage" },
  "linux-x64": { extension: "rpm", contentType: "application/x-rpm" }
} as const satisfies Record<DesktopUpdateTarget, { extension: string; contentType: string }>;

export const desktopUpdateAssetSchema = z.object({
  filename: z.string().min(1).max(180).regex(/^[A-Za-z0-9][A-Za-z0-9._ -]*$/, "Update filenames may not contain path separators"),
  byteLength: z.number().int().positive().max(1024 * 1024 * 1024),
  sha256: z.string().regex(/^[a-f0-9]{64}$/)
}).strict();
export type DesktopUpdateAsset = z.infer<typeof desktopUpdateAssetSchema>;

export const desktopUpdateManifestSchema = z.object({
  schemaVersion: z.literal(1),
  version: desktopVersionSchema,
  publishedAt: z.iso.datetime(),
  assets: z.object({
    "win32-x64": desktopUpdateAssetSchema,
    "darwin-universal": desktopUpdateAssetSchema,
    "linux-x64": desktopUpdateAssetSchema
  }).strict()
}).strict();
export type DesktopUpdateManifest = z.infer<typeof desktopUpdateManifestSchema>;

export const desktopUpdateCatalogResponseSchema = z.discriminatedUnion("available", [
  z.object({ available: z.literal(false) }).strict(),
  z.object({
    available: z.literal(true),
    version: desktopVersionSchema,
    publishedAt: z.iso.datetime(),
    target: desktopUpdateTargetSchema,
    asset: desktopUpdateAssetSchema
  }).strict()
]);
export type DesktopUpdateCatalogResponse = z.infer<typeof desktopUpdateCatalogResponseSchema>;

export interface DesktopUpdateInfo {
  version: string;
  publishedAt: string;
  target: DesktopUpdateTarget;
  asset: DesktopUpdateAsset;
}

export type DesktopUpdateState =
  | { phase: "idle" | "checking" | "current"; currentVersion: string }
  | { phase: "available" | "ready" | "installing" | "installed"; currentVersion: string; update: DesktopUpdateInfo }
  | { phase: "downloading"; currentVersion: string; update: DesktopUpdateInfo; receivedBytes: number; totalBytes: number }
  | { phase: "error"; currentVersion: string; update?: DesktopUpdateInfo; message: string };

/** Who the desktop app's sign-in belongs to, as the Worker sees it. */
export const authCheckResponseSchema = z.object({ userId: z.string().min(1).max(256) }).strict();
export type AuthCheckResponse = z.infer<typeof authCheckResponseSchema>;
